import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle,
  Plus,
  X,
  Search,
  Trash2,
  Loader2,
  Package,
  TrendingDown,
  Calendar,
} from "lucide-react";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtDate = (iso) => (iso || "").slice(0, 16).replace("T", " ");

const RANGES = [
  { key: 1, label: "آخر يوم" },
  { key: 3, label: "آخر 3 أيام" },
  { key: 30, label: "آخر 30 يوم" },
  { key: 0, label: "الكل" },
];

const ProductPicker = ({ products, onPick }) => {
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return products.slice(0, 14);
    return products.filter((p) => (p.name_ar || "").toLowerCase().includes(t)).slice(0, 30);
  }, [products, q]);
  return (
    <div data-testid="damage-product-picker">
      <div className="relative mb-2">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="ابحث عن منتج تالف..."
          data-testid="damage-product-search"
          className="w-full pr-9 pl-4 py-2 rounded-full bg-white border-2 border-[#E85D45] text-sm focus:outline-none"
        />
      </div>
      <div className="max-h-72 overflow-y-auto space-y-1.5">
        {list.length === 0 ? (
          <p className="text-center text-sm text-[#5C6E64] py-4">لا توجد نتائج</p>
        ) : list.map((p) => (
          <button
            key={p.id}
            onClick={() => onPick(p)}
            data-testid={`pick-damage-product-${p.id}`}
            className="w-full bg-white hover:bg-[#FEE2E2] border border-[#E4EBE6] hover:border-[#E85D45] rounded-xl p-2 flex items-center gap-2 text-right transition-colors"
          >
            {p.image_path ? (
              <img src={`${API}/files/${p.image_path}`} alt="" className="w-10 h-10 object-cover rounded-lg shrink-0" />
            ) : (
              <div className="w-10 h-10 bg-[#F3F6F4] rounded-lg flex items-center justify-center shrink-0">
                <Package className="w-4 h-4 text-[#5C6E64]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-[#111814] truncate">{p.name_ar}</p>
              <p className="text-[10px] text-[#5C6E64]">
                المخزن: <span dir="ltr" className="font-bold">{p.stock}</span>
                <span className="mx-1.5">·</span>
                تكلفة: <span dir="ltr">{fmt(p.cost_price)}</span>
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const NewDamageModal = ({ onClose, onSaved }) => {
  const [products, setProducts] = useState([]);
  const [picker, setPicker] = useState(true);
  const [chosen, setChosen] = useState(null);
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/products`);
        setProducts(data || []);
      } catch { /* silent */ }
    })();
  }, []);

  const submit = async () => {
    if (!chosen) { toast.error("اختر منتجاً"); return; }
    const n = parseInt(qty);
    if (!n || n <= 0) { toast.error("أدخل كمية صحيحة"); return; }
    if (n > (chosen.stock || 0)) { toast.error(`الكمية أكبر من المخزن (${chosen.stock})`); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/damaged-items`, {
        product_id: chosen.id,
        quantity: n,
        reason: reason.trim(),
      });
      toast.success("تم تسجيل التلف وخصم الكميّة من المخزن");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التسجيل");
    } finally {
      setSaving(false);
    }
  };

  const totalLoss = chosen ? (parseInt(qty) || 0) * (chosen.cost_price || 0) : 0;

  return (
    <div className="fixed inset-0 z-[130] bg-black/60 flex items-center justify-center p-3" onClick={onClose} data-testid="new-damage-modal" dir="rtl">
      <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-l from-[#E85D45] to-[#d54d35] text-white p-4 flex justify-between items-center">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> تسجيل تلف مواد
          </h3>
          <button onClick={onClose} data-testid="new-damage-close" className="bg-white/20 hover:bg-white/30 rounded-full p-1.5">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {picker ? (
            <ProductPicker products={products} onPick={(p) => { setChosen(p); setPicker(false); }} />
          ) : (
            <>
              <div className="bg-[#FEE2E2] border-2 border-[#E85D45]/40 rounded-xl p-3 flex items-center gap-2">
                {chosen.image_path ? (
                  <img src={`${API}/files/${chosen.image_path}`} alt="" className="w-12 h-12 object-cover rounded-lg" />
                ) : (
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center"><Package className="w-5 h-5" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">{chosen.name_ar}</p>
                  <p className="text-[10px] text-[#5C6E64]">
                    المخزن: <span dir="ltr" className="font-bold">{chosen.stock}</span>
                    <span className="mx-1.5">·</span>
                    تكلفة الوحدة: <span dir="ltr">{fmt(chosen.cost_price)}</span>
                  </p>
                </div>
                <button onClick={() => { setChosen(null); setPicker(true); setQty(""); }} className="text-xs text-[#1E3A5F] underline">تغيير</button>
              </div>
              <div>
                <label className="text-xs font-bold text-[#5C6E64] block mb-1">الكميّة التالفة (كراتين)</label>
                <input
                  type="number"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  min={1}
                  max={chosen.stock || 999999}
                  data-testid="damage-qty"
                  className="w-full px-3 py-3 rounded-xl border-2 border-[#E85D45] text-lg font-bold text-center"
                  dir="ltr"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-[#5C6E64] block mb-1">السبب (اختياري)</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  data-testid="damage-reason"
                  placeholder="مثال: انتهاء الصلاحية، كسر، رطوبة..."
                  className="w-full px-3 py-2 rounded-xl border-2 border-[#E4EBE6] focus:border-[#E85D45] focus:outline-none"
                />
              </div>
              <div className="bg-[#FFF7ED] border border-[#FDBA74] rounded-xl p-3 text-center">
                <p className="text-[10px] text-[#5C6E64]">إجمالي الخسارة</p>
                <p className="text-2xl font-black text-[#E85D45]" data-testid="damage-total-loss" dir="ltr">{fmt(totalLoss)} د.ع</p>
              </div>
            </>
          )}
        </div>
        {!picker && (
          <div className="p-3 border-t-2 border-[#E4EBE6] flex justify-end gap-2">
            <button onClick={onClose} className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-2 px-4 rounded-full">إلغاء</button>
            <button
              onClick={submit}
              disabled={saving}
              data-testid="damage-submit"
              className="bg-[#E85D45] hover:bg-[#d54d35] disabled:opacity-50 text-white font-bold py-2 px-5 rounded-full flex items-center gap-1"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
              تسجيل التلف
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default function DamagedItemsTab() {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ total_loss: 0, total_quantity: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(3);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (days && days > 0) params.days = days;
      const { data } = await axios.get(`${API}/admin/damaged-items`, { params });
      setItems(data.items || []);
      setTotals({
        total_loss: data.total_loss || 0,
        total_quantity: data.total_quantity || 0,
        count: data.count || 0,
      });
    } catch { toast.error("فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [days]);

  const removeEntry = async (id) => {
    if (!(await confirmDialog("التراجع عن هذا التلف سيُعيد الكميّة للمخزن. متأكد؟", { tone: "danger", force: true }))) return;
    try {
      await axios.delete(`${API}/admin/damaged-items/${id}`);
      toast.success("تم التراجع — رجعت الكميّة للمخزن");
      load();
    } catch { toast.error("فشل"); }
  };

  return (
    <div className="space-y-3" dir="rtl" data-testid="damaged-items-tab">
      {/* Header */}
      <div className="bg-gradient-to-l from-[#E85D45] to-[#d54d35] text-white rounded-2xl p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-6 h-6" />
            <div>
              <h3 className="font-bold text-lg">تلف مواد</h3>
              <p className="text-xs opacity-90">سجّل المواد التالفة. التكلفة تُخصم من أرباح التطبيق.</p>
            </div>
          </div>
          <button
            onClick={() => setShowNew(true)}
            data-testid="new-damage-btn"
            className="bg-white text-[#E85D45] hover:bg-[#FEE2E2] font-bold py-2 px-4 rounded-full flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> تسجيل تلف جديد
          </button>
        </div>
      </div>

      {/* Range filter */}
      <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6]" data-testid="damaged-ranges">
        <div className="flex flex-wrap gap-1.5">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setDays(r.key)}
              data-testid={`damaged-range-${r.key}`}
              className={`text-xs font-bold py-1.5 px-3 rounded-full transition-colors ${
                days === r.key ? "bg-[#E85D45] text-white" : "bg-[#F3F6F4] text-[#5C6E64] hover:bg-[#E4EBE6]"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] text-center" data-testid="damaged-total-loss">
          <TrendingDown className="w-5 h-5 mx-auto text-[#E85D45] mb-1" />
          <p className="text-[10px] text-[#5C6E64]">إجمالي الخسارة</p>
          <p className="text-lg font-black text-[#E85D45]" dir="ltr">{fmt(totals.total_loss)}</p>
        </div>
        <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] text-center" data-testid="damaged-total-qty">
          <Package className="w-5 h-5 mx-auto text-[#F59E0B] mb-1" />
          <p className="text-[10px] text-[#5C6E64]">الكميّة التالفة</p>
          <p className="text-lg font-black text-[#F59E0B]" dir="ltr">{fmt(totals.total_quantity)}</p>
        </div>
        <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] text-center" data-testid="damaged-total-count">
          <AlertTriangle className="w-5 h-5 mx-auto text-[#1E3A5F] mb-1" />
          <p className="text-[10px] text-[#5C6E64]">عدد السجلات</p>
          <p className="text-lg font-black text-[#1E3A5F]" dir="ltr">{fmt(totals.count)}</p>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white border-2 border-dashed rounded-2xl p-10 text-center text-[#5C6E64]" data-testid="damaged-empty">
          <AlertTriangle className="w-10 h-10 mx-auto opacity-30 mb-2" />
          لا توجد سجلات تلف في هذه الفترة
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map((d) => (
            <div
              key={d.id}
              data-testid={`damaged-row-${d.id}`}
              className="bg-white border-2 border-[#FEE2E2] rounded-xl p-2.5 flex items-center gap-2"
            >
              {d.product_image_path ? (
                <img src={`${API}/files/${d.product_image_path}`} alt="" className="w-12 h-12 object-cover rounded-lg" />
              ) : (
                <div className="w-12 h-12 bg-[#FEE2E2] rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-[#E85D45]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate">{d.product_name_ar}</p>
                <p className="text-[10px] text-[#5C6E64]">
                  <Calendar className="w-3 h-3 inline ml-1" /><span dir="ltr">{fmtDate(d.created_at)}</span>
                  <span className="mx-1.5">·</span>
                  <span>{d.declared_by_name || "—"}</span>
                </p>
                {d.reason && <p className="text-[10px] text-[#5C6E64] mt-0.5 italic">السبب: {d.reason}</p>}
              </div>
              <div className="text-left shrink-0">
                <p className="text-[10px] text-[#5C6E64]">×<span dir="ltr">{d.quantity}</span></p>
                <p className="font-black text-[#E85D45]" dir="ltr">{fmt(d.total_cost)}</p>
              </div>
              <button
                onClick={() => removeEntry(d.id)}
                data-testid={`damaged-delete-${d.id}`}
                className="text-[#E85D45] hover:bg-[#FEE2E2] rounded p-1.5 shrink-0"
                title="تراجع وإرجاع المخزن"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {showNew && <NewDamageModal onClose={() => setShowNew(false)} onSaved={load} />}
    </div>
  );
}
